import React, { useState, useEffect, useRef } from "react";
import SongCard from "./SongCard";
import {
  FaFastBackward,
  FaFastForward,
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward
} from "react-icons/fa";
import { BsMusicNoteList } from "react-icons/bs";
import songData from "../bin/songs.json";
import "./AudioPlayer.css";

function AudioPlaylist() {
  const [playlist, setPlaylist] = useState([]);
  const [currentTrack, setTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const newPlaylist = songData.map((song) => ({
      src: song.src,
      id: song.id,
      title: song.title,
      band: song.band,
      album: song.album,
      year: song.year,
      active: song.active !== undefined ? song.active : true
    }));
    setPlaylist(newPlaylist);
  }, []);

  useEffect(() => {
    const audioElement = audioRef.current;

    if (!audioElement) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    // Add event listeners to sync custom play/pause button with native controls
    audioElement.addEventListener("play", handlePlay);
    audioElement.addEventListener("pause", handlePause);

    return () => {
      audioElement.removeEventListener("play", handlePlay);
      audioElement.removeEventListener("pause", handlePause);
    };
  }, []);

  const handleClickNext = () => {
    setTrackIndex((currentTrack) =>
      currentTrack < playlist.length - 1 ? currentTrack + 1 : 0
    );
    setIsPlaying(true);
  };

  const handleClickPrev = () => {
    setTrackIndex((currentTrack) =>
      currentTrack > 0 ? currentTrack - 1 : playlist.length - 1
    );
    setIsPlaying(true);
  };

  const handleEnd = () => {
    setTrackIndex((currentTrack) =>
      currentTrack < playlist.length - 1 ? currentTrack + 1 : 0
    );
    setIsPlaying(true);
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch((error) => {
          console.warn("Autoplay was prevented by the browser.");
        });
      }
    }
  };

  const handleRewind = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        audioRef.current.currentTime - 10
      );
    }
  };

  const handleFastForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration,
        audioRef.current.currentTime + 10
      );
    }
  };

  const handlePlaySong = (id, active) => {
    if (!active) return;

    setTrackIndex(playlist.findIndex((song) => song.id === id));
    setIsPlaying(true);
  };

  const handleUpdatePlaylist = (song, activeStatus) => {
    if (activeStatus) {
      addSong(song);
    } else {
      removeSong(song.id);
    }
  };

  const addSong = (songToAdd) => {
    if (songToAdd && !playlist.some((song) => song.id === songToAdd.id)) {
      const newPlaylist = [
        ...playlist,
        {
          id: songToAdd.id,
          src: songToAdd.src,
          title: songToAdd.title,
          band: songToAdd.band,
          album: songToAdd.album,
          year: songToAdd.year
        }
      ];
      orderList(newPlaylist);
    }
  };

  const removeSong = (id) => {
    const newPlaylist = playlist
      .filter((song) => song.id !== id)
      .map((song) => ({
        id: song.id,
        src: song.src,
        title: song.title,
        band: song.band,
        album: song.album,
        year: song.year
      }));
    orderList(newPlaylist);

    // If the currently playing song is removed, stop playback and go to the next track.
    if (playlist[currentTrack]?.id === id) {
      setTrackIndex((currentTrack) =>
        currentTrack < newPlaylist.length ? currentTrack : 0
      );
      setIsPlaying(false);
    }
  };

  const orderList = (newPlaylist) => {
    const orderedPlaylist = [];

    songData.forEach((song) => {
      const foundInNewPlaylist = newPlaylist.find(
        (newSong) => newSong.id === song.id
      );

      if (foundInNewPlaylist) {
        orderedPlaylist.push({
          id: foundInNewPlaylist.id,
          src: foundInNewPlaylist.src,
          title: foundInNewPlaylist.title,
          band: foundInNewPlaylist.band,
          album: foundInNewPlaylist.album,
          year: foundInNewPlaylist.year
        });
      }
    });

    setPlaylist(orderedPlaylist);
  };

  // Play the current track whenever the current track index changes, if there was a user interaction
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Audio is playing
          })
          .catch((error) => {
            // Autoplay was prevented
            console.warn("Autoplay was prevented by the browser.", error);
            setIsPlaying(false);
          });
      }
    }
  }, [currentTrack]);

  return (
    <div className="w-full mx-auto p-2 bg-gray-800">
      <div>
        {/* Digital Display */}
        <div className="digital-display-container mb-4">
          <div className="digital-display">
            {playlist.length > 0
              ? `${playlist[currentTrack]?.band} - ${playlist[currentTrack]?.title} [${playlist[currentTrack]?.album} : ${playlist[currentTrack]?.year}]`
              : "No song playing"}
          </div>
        </div>
        <audio
          ref={audioRef}
          controls
          onEnded={handleEnd}
          src={playlist.length > 0 ? playlist[currentTrack]?.src : ""}
          className="w-full mb-2"
        >
          Your browser does not support the audio element.
        </audio>
        <div className="flex justify-center space-x-8 mb-2">
          <button
            onClick={handleClickPrev}
            className="p-2 bg-gray-800 text-white rounded"
          >
            <FaStepBackward />
          </button>
          <button
            onClick={handleRewind}
            className="p-2 bg-gray-800 text-white rounded"
          >
            <FaFastBackward />
          </button>
          <button
            onClick={handlePlayPause}
            className="p-2 bg-gray-800 text-white rounded"
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button
            onClick={handleFastForward}
            className="p-2 bg-gray-800 text-white rounded"
          >
            <FaFastForward />
          </button>
          <button
            onClick={handleClickNext}
            className="p-2 bg-gray-800 text-white rounded"
          >
            <FaStepForward />
          </button>
        </div>
      </div>
      <div className="flex text-[10px] items-center">
        * Click the <BsMusicNoteList className="mx-1 inline-block" /> icon to
        skip song as it plays through list
      </div>

      {songData.map((song, index) => (
        <SongCard
          key={song.id}
          song={song}
          index={index}
          handleUpdatePlaylist={handleUpdatePlaylist}
          handlePlaySong={handlePlaySong}
        />
      ))}
    </div>
  );
}

export default AudioPlaylist;
